import { useTranslation } from "react-i18next";
import { Checkbox } from "shared/components/ui/checkbox";
import constraintsStore from "shared/store/constraintsStore";
import mainStyles from "style/main.module.scss";
import { ConstraintConfigType } from "types/ConstraintsTypes";
import { esgPreferenceMapEngToIta, esgPreferenceMapItaToEng } from "utils/misc";
import { Dropdown } from "../../Dropdown";

type EsgContentProps = {
  configData: ConstraintConfigType;
};

// Helper type to extract boolean values from config data
type EsgConfigData = {
  esg_preference: { default: string; type: "string" };
  lettera_a: { default: boolean; type: "boolean" };
  lettera_b: { default: boolean; type: "boolean" };
  lettera_c: { default: boolean; type: "boolean" };
  pai_e: { default: boolean; type: "boolean" };
  pai_s: { default: boolean; type: "boolean" };
  pai_g: { default: boolean; type: "boolean" };
};

export function EsgContent({ configData }: EsgContentProps) {
  const { t } = useTranslation();
  // Type assertion for configData to ensure we're working with boolean defaults
  const typedConfigData = configData as unknown as EsgConfigData;

  const esg = constraintsStore((s) => s.workingDocument?.esg);

  // Derive display values from the working document (English) or fall back to configData defaults
  const esg_preference_display = esg?.esg_preference
    ? esgPreferenceMapEngToIta(esg.esg_preference as string) ||
      typedConfigData.esg_preference.default
    : typedConfigData.esg_preference.default;

  const lettera_a = esg?.lettera_a !== undefined
    ? (esg.lettera_a as boolean)
    : typedConfigData.lettera_a.default;

  const lettera_b = esg?.lettera_b !== undefined
    ? (esg.lettera_b as boolean)
    : typedConfigData.lettera_b.default;

  const lettera_c = esg?.lettera_c !== undefined
    ? (esg.lettera_c as boolean)
    : typedConfigData.lettera_c.default;

  const pai_e = esg?.pai_e !== undefined
    ? (esg.pai_e as boolean)
    : typedConfigData.pai_e.default;

  const pai_s = esg?.pai_s !== undefined
    ? (esg.pai_s as boolean)
    : typedConfigData.pai_s.default;

  const pai_g = esg?.pai_g !== undefined
    ? (esg.pai_g as boolean)
    : typedConfigData.pai_g.default;

  return (
    <>
      <div style={{ width: "100%", marginTop: 0 }}>
        <div
          style={{
            marginBottom: 24,
          }}
        >
          <Dropdown
            label={configData.esg_preference.label}
            options={configData.esg_preference.values}
            value={esg_preference_display}
            onChange={(selected_ita) => {
              const revertingToDefault =
                selected_ita === typedConfigData.esg_preference.default;
              // When the user returns to "Nessuna Preferenza", the Lettera
              // A/B/C checkboxes (and, by transitivity, PAI E/S/G) become
              // disabled in the UI but their stored booleans would otherwise
              // survive — and ship to the agent / persist into a profile —
              // even though the user has explicitly opted out. Mirror the
              // Lettera C "No" cascade below: reset every dependent in the
              // same atomic group edit so modified-detection sees one diff.
              constraintsStore.getState().applyGroupEdits("esg", {
                esg_preference: esgPreferenceMapItaToEng(selected_ita),
                ...(revertingToDefault && {
                  lettera_a: false,
                  lettera_b: false,
                  lettera_c: false,
                  pai_e: false,
                  pai_s: false,
                  pai_g: false,
                }),
              });
            }}
          />
        </div>

        <div className={mainStyles.gridContainer2x3}>
          <div className={mainStyles.gridItem}>
            {configData.lettera_a.label}
          </div>
          <div className={mainStyles.gridItem}>
            <div className="flex items-center space-x-2">
              <label className={mainStyles.label}>
                {t("constraints.yes")}
              </label>
              <Checkbox
                id="lettera_a_yes"
                checked={lettera_a}
                disabled={
                  esg_preference_display ===
                  typedConfigData.esg_preference.default
                }
                onCheckedChange={(checked) => {
                  constraintsStore.getState().applyGroupEdits("esg", {
                    lettera_a: checked === true,
                  });
                }}
              />
            </div>
          </div>
          <div className={mainStyles.gridItem}>
            <div className="flex items-center space-x-2">
              <label className={mainStyles.label}>
                {t("constraints.no")}
              </label>
              <Checkbox
                id="lettera_a_no"
                checked={!lettera_a}
                disabled={
                  esg_preference_display ===
                  typedConfigData.esg_preference.default
                }
                onCheckedChange={(checked) => {
                  constraintsStore.getState().applyGroupEdits("esg", {
                    lettera_a: checked === false,
                  });
                }}
              />
            </div>
          </div>
        </div>

        <div className={mainStyles.gridContainer2x3}>
          <div className={mainStyles.gridItem}>
            {configData.lettera_b.label}
          </div>
          <div className={mainStyles.gridItem}>
            <div className="flex items-center space-x-2">
              <label className={mainStyles.label}>
                {t("constraints.yes")}
              </label>
              <Checkbox
                id="lettera_b_yes"
                checked={lettera_b}
                disabled={
                  esg_preference_display ===
                  typedConfigData.esg_preference.default
                }
                onCheckedChange={(checked) => {
                  constraintsStore.getState().applyGroupEdits("esg", {
                    lettera_b: checked === true,
                  });
                }}
              />
            </div>
          </div>
          <div className={mainStyles.gridItem}>
            <div className="flex items-center space-x-2">
              <label className={mainStyles.label}>
                {t("constraints.no")}
              </label>
              <Checkbox
                id="lettera_b_no"
                checked={!lettera_b}
                disabled={
                  esg_preference_display ===
                  typedConfigData.esg_preference.default
                }
                onCheckedChange={(checked) => {
                  constraintsStore.getState().applyGroupEdits("esg", {
                    lettera_b: checked === false,
                  });
                }}
              />
            </div>
          </div>
        </div>

        <div className={mainStyles.gridContainer2x3}>
          <div className={mainStyles.gridItem}>
            {configData.lettera_c.label}
          </div>
          <div className={mainStyles.gridItem}>
            <div className="flex items-center space-x-2">
              <label className={mainStyles.label}>
                {t("constraints.yes")}
              </label>
              <Checkbox
                id="lettera_c_yes"
                checked={lettera_c}
                disabled={
                  esg_preference_display ===
                  typedConfigData.esg_preference.default
                }
                onCheckedChange={(checked) => {
                  constraintsStore.getState().applyGroupEdits("esg", {
                    lettera_c: checked === true,
                  });
                }}
              />
            </div>
          </div>
          <div className={mainStyles.gridItem}>
            <div className="flex items-center space-x-2">
              <label className={mainStyles.label}>
                {t("constraints.no")}
              </label>
              <Checkbox
                id="lettera_c_no"
                checked={!lettera_c}
                disabled={
                  esg_preference_display ===
                  typedConfigData.esg_preference.default
                }
                onCheckedChange={(checked) => {
                  // When lettera_c is set to false (checked is true for the NO box),
                  // also reset PAI values — single atomic update
                  if (checked === true) {
                    constraintsStore.getState().applyGroupEdits("esg", {
                      lettera_c: false,
                      pai_e: false,
                      pai_s: false,
                      pai_g: false,
                    });
                  } else {
                    constraintsStore.getState().applyGroupEdits("esg", {
                      lettera_c: checked === false,
                    });
                  }
                }}
              />
            </div>
          </div>
        </div>

        <div className={mainStyles.gridContainer2x3}>
          <div className={mainStyles.gridItem}>{configData.pai_e.label}</div>
          <div className={mainStyles.gridItem}>
            <div className="flex items-center space-x-2">
              <label className={mainStyles.label}>
                {t("constraints.yes")}
              </label>
              <Checkbox
                id="pai_e_yes"
                checked={pai_e}
                disabled={!lettera_c}
                onCheckedChange={(checked) => {
                  constraintsStore.getState().applyGroupEdits("esg", {
                    pai_e: checked === true,
                  });
                }}
              />
            </div>
          </div>
          <div className={mainStyles.gridItem}>
            <div className="flex items-center space-x-2">
              <label className={mainStyles.label}>
                {t("constraints.no")}
              </label>
              <Checkbox
                id="pai_e_no"
                checked={!pai_e}
                disabled={!lettera_c}
                onCheckedChange={(checked) => {
                  constraintsStore.getState().applyGroupEdits("esg", {
                    pai_e: checked === false,
                  });
                }}
              />
            </div>
          </div>
        </div>

        <div className={mainStyles.gridContainer2x3}>
          <div className={mainStyles.gridItem}>{configData.pai_s.label}</div>
          <div className={mainStyles.gridItem}>
            <div className="flex items-center space-x-2">
              <label className={mainStyles.label}>
                {t("constraints.yes")}
              </label>
              <Checkbox
                id="pai_s_yes"
                checked={pai_s}
                disabled={!lettera_c}
                onCheckedChange={(checked) => {
                  constraintsStore.getState().applyGroupEdits("esg", {
                    pai_s: checked === true,
                  });
                }}
              />
            </div>
          </div>
          <div className={mainStyles.gridItem}>
            <div className="flex items-center space-x-2">
              <label className={mainStyles.label}>
                {t("constraints.no")}
              </label>
              <Checkbox
                id="pai_s_no"
                checked={!pai_s}
                disabled={!lettera_c}
                onCheckedChange={(checked) => {
                  constraintsStore.getState().applyGroupEdits("esg", {
                    pai_s: checked === false,
                  });
                }}
              />
            </div>
          </div>
        </div>

        <div className={mainStyles.gridContainer1x3Esg}>
          <div className={mainStyles.gridItem}>{configData.pai_g.label}</div>
          <div className={mainStyles.gridItem}>
            <div className="flex items-center space-x-2">
              <label className={mainStyles.label}>
                {t("constraints.yes")}
              </label>
              <Checkbox
                id="pai_g_yes"
                checked={pai_g}
                disabled={!lettera_c}
                onCheckedChange={(checked) => {
                  constraintsStore.getState().applyGroupEdits("esg", {
                    pai_g: checked === true,
                  });
                }}
              />
            </div>
          </div>
          <div className={mainStyles.gridItem}>
            <div className="flex items-center space-x-2">
              <label className={mainStyles.label}>
                {t("constraints.no")}
              </label>
              <Checkbox
                id="pai_g_no"
                checked={!pai_g}
                disabled={!lettera_c}
                onCheckedChange={(checked) => {
                  constraintsStore.getState().applyGroupEdits("esg", {
                    pai_g: checked === false,
                  });
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
